import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { fetchAccess } from '../Api';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

export default function VideoPlayer({ videoId, category }) {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(null); // null = loading, false = denied, true = allowed
  const [videoBlobUrl, setVideoBlobUrl] = useState('');

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let abortController = new AbortController();

    const checkAccess = async (firebaseUser) => {
      const userId = localStorage.getItem('loggedInUserId');
      if (!userId) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetchAccess(userId);
        if (response.status === 200 && Array.isArray(response.data)) {
          const hasAccess = response.data.some(
            (item) =>
              item.course_category?.trim().toLowerCase() === category?.trim().toLowerCase() &&
              parseInt(item.giveAccess) === 1
          );

          if (hasAccess && firebaseUser) {
              const idToken = await firebaseUser.getIdToken();

              const videoResponse = await fetch(
                `${process.env.REACT_APP_APIURL}secure-video?id=${encodeURIComponent(videoId)}`,
                {
                  method: 'GET',
                  headers: {
                    Authorization: `Bearer ${idToken}`,
                  },
                  signal: abortController.signal,
                }
              );

              if (!videoResponse.ok) {
                setIsAuthorized(false);
                setIsLoading(false);
                return;
              }

              const videoBlob = await videoResponse.blob();
              const blobUrl = URL.createObjectURL(videoBlob);
              setVideoBlobUrl(blobUrl);

              setIsAuthorized(true);
          } else {
            setIsAuthorized(false);
          }
        } else {
          setIsAuthorized(false);
        }
      } catch (err) {
        console.error('Access check failed:', err);
        setIsAuthorized(false);
      } finally {
        setIsLoading(false); // ensure loading stops
      }
    };

    const auth = getAuth();

    // Wait for Firebase to load the user after refresh
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        checkAccess(firebaseUser);
      } else {
        navigate('/login');
      }
    });

    return () => {
      abortController.abort(); // cleanup fetch on unmount
      unsubscribe();
      if (videoBlobUrl) {
        URL.revokeObjectURL(videoBlobUrl); // Clean up blob URL
      }
    };
  }, [videoId, category, navigate]);

  // console.log("Secure video URL: ", videoBlobUrl);
  // if (isLoading) return <div>Loading Please wait...</div>;
  // if (isAuthorized === false) return <div className="text-danger">Lecture Access denied.!</div>;
  // if (!videoId || !videoBlobUrl) return <div className="text-danger">No video selected or available.</div>;
  if (isLoading) {
      return (
        <div className="d-flex flex-column justify-content-center align-items-center videoLoadingFrame">
          {/* Skeleton frame with pulse animation */}
          <div
            className="position-relative mb-1 w-100"
            style={{
              height: '700px',
              backgroundColor: '#f0f0f0',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <div className="skeleton-loader w-100 h-100"></div>
          
            {/* Spinner */}
            <div className="position-absolute top-50 start-50 translate-middle d-flex flex-column align-items-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted text-center"><strong>Loading video, please wait...!</strong></p>
            </div>
          </div>
        </div>
      );
  }

  if (isAuthorized === false) {
      return (
        <div className="d-flex flex-column justify-content-center align-items-center videoLoadingFrame">
          {/* Skeleton frame*/}
          <div
            className="position-relative mb-1 w-100"
            style={{
              height: '700px',
              backgroundColor: '#f0f0f0',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <div className="position-absolute top-50 start-50 translate-middle d-flex flex-column align-items-center">
              <h5 className="mt-3 text-center text-danger"><strong>Lecture Access denied.!</strong></h5>
            </div>
          </div>
        </div>
      );
  }

  if (!videoId || !videoBlobUrl) {
      return (
        <div className="d-flex flex-column justify-content-center align-items-center videoLoadingFrame">
          {/* Skeleton frame */}
          <div
            className="position-relative mb-1 w-100"
            style={{
              height: '700px',
              backgroundColor: '#f0f0f0',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <div className="position-absolute top-50 start-50 translate-middle d-flex flex-column align-items-center">
              <h5 className="mt-3 text-center text-danger"><strong>No video selected or available..!</strong></h5>
            </div>
          </div>
        </div>
      );
  }

  return (
    <div className="ratio ratio-16x9 bg-dark video rounded-2">
      <video
        src={videoBlobUrl}
        controls
        controlsList="nodownload"
        disablePictureInPicture
        preload="metadata"
        onContextMenu={(e) => e.preventDefault()}
        className="w-100 rounded shadow"
      />
    </div>
  );
}

VideoPlayer.propTypes = {
  videoId: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
};
